import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";

// Core pages - loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AdminLoginPage from "./pages/AdminLoginPage";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import WorkspaceRoute from "./components/workspace/WorkspaceRoute";
import PostLoginRouter from "./components/routing/PostLoginRouter";

// Lazy loaded pages - code split by route groups
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PartnershipsPage = lazy(() => import("./pages/PartnershipsPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const ScheduleMeetingPage = lazy(() => import("./pages/ScheduleMeetingPage"));
const VerifyCertificatePage = lazy(() => import("./pages/VerifyCertificatePage"));

// Service pages - grouped chunk
const ISOCertificationsPage = lazy(() => import("./pages/services/ISOCertificationsPage"));
const CSRImpactPage = lazy(() => import("./pages/services/CSRImpactPage"));
const SkillDevelopmentPage = lazy(() => import("./pages/services/SkillDevelopmentPage"));
const ResearchSurveysPage = lazy(() => import("./pages/services/ResearchSurveysPage"));
const ManagementConsultingPage = lazy(() => import("./pages/services/ManagementConsultingPage"));

// Tool pages - grouped chunk
const ToolsIndexPage = lazy(() => import("./pages/tools/ToolsIndexPage"));
const ComplianceAdvisorPage = lazy(() => import("./pages/tools/ComplianceAdvisorPage"));
const ReadinessCheckerPage = lazy(() => import("./pages/tools/ReadinessCheckerPage"));
const CSRGeneratorPage = lazy(() => import("./pages/tools/CSRGeneratorPage"));
const ImpactEstimatorPage = lazy(() => import("./pages/tools/ImpactEstimatorPage"));

// Admin pages - protected chunk
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// Workspace pages - workspace chunk
const WorkspaceDashboard = lazy(() => import("./pages/workspace/WorkspaceDashboard"));
const ProjectsPage = lazy(() => import("./pages/workspace/ProjectsPage"));
const ProjectDetailsPage = lazy(() => import("./pages/workspace/ProjectDetailsPage"));
const ServiceRequestsPage = lazy(() => import("./pages/workspace/ServiceRequestsPage"));
const DocumentsPage = lazy(() => import("./pages/workspace/DocumentsPage"));
const WorkspaceToolsPage = lazy(() => import("./pages/workspace/WorkspaceToolsPage"));
const WorkspaceSettingsPage = lazy(() => import("./pages/workspace/WorkspaceSettingsPage"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <PostLoginRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/iso-certifications" element={<ISOCertificationsPage />} />
                <Route path="/services/iso-certifications/:standard" element={<ISOCertificationsPage />} />
                <Route path="/services/csr-impact" element={<CSRImpactPage />} />
                <Route path="/services/skill-development" element={<SkillDevelopmentPage />} />
                <Route path="/services/research-surveys" element={<ResearchSurveysPage />} />
                <Route path="/services/management-consulting" element={<ManagementConsultingPage />} />
                <Route path="/partnerships" element={<PartnershipsPage />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/schedule" element={<ScheduleMeetingPage />} />
                <Route path="/tools" element={<ToolsIndexPage />} />
                <Route path="/tools/compliance-advisor" element={<ComplianceAdvisorPage />} />
                <Route path="/tools/readiness-checker" element={<ReadinessCheckerPage />} />
                <Route path="/tools/csr-generator" element={<CSRGeneratorPage />} />
                <Route path="/tools/impact-estimator" element={<ImpactEstimatorPage />} />
                <Route path="/privacy" element={<LegalPage page="privacy" />} />
                <Route path="/terms" element={<LegalPage page="terms" />} />
                <Route path="/refund" element={<LegalPage page="refund" />} />
                <Route path="/disclaimer" element={<LegalPage page="disclaimer" />} />
                <Route path="/faq" element={<LegalPage page="faq" />} />
                <Route path="/delivery" element={<LegalPage page="delivery" />} />
                <Route path="/verify/:certificateId" element={<VerifyCertificatePage />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                {/* Workspace Routes */}
                <Route path="/workspace" element={<WorkspaceRoute><WorkspaceDashboard /></WorkspaceRoute>} />
                <Route path="/workspace/projects" element={<WorkspaceRoute><ProjectsPage /></WorkspaceRoute>} />
                <Route path="/workspace/projects/:projectId" element={<WorkspaceRoute><ProjectDetailsPage /></WorkspaceRoute>} />
                <Route path="/workspace/requests" element={<WorkspaceRoute><ServiceRequestsPage /></WorkspaceRoute>} />
                <Route path="/workspace/documents" element={<WorkspaceRoute><DocumentsPage /></WorkspaceRoute>} />
                <Route path="/workspace/tools" element={<WorkspaceRoute><WorkspaceToolsPage /></WorkspaceRoute>} />
                <Route path="/workspace/settings" element={<WorkspaceRoute><WorkspaceSettingsPage /></WorkspaceRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PostLoginRouter>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
